import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useFeatureGate } from '../../hooks/useFeatureGate';
import { ROUTE } from '../../routes';
import { WEBSITE_HUB_FLAG } from './tabs';

/** The Website section's first available tab, in tab order: Website Creatives, then Logos. Website Creatives
 *  gates on a rollback switch; Logos, the next tab, is flagless, so it is the guaranteed terminal and the tabs
 *  after it (Game Lists, Blog) are never the landing tab. Pure (takes the gate's `isEnabled` predicate) so the
 *  rollback branch is unit-testable without a localStorage reveal, which can only turn a flag ON. */
export const firstWebsiteTab = (isEnabled: (key: string) => boolean): string =>
  isEnabled('creatives') ? ROUTE.creatives() : ROUTE.logos();

/** `/account/website` lands on {@link firstWebsiteTab}. */
export const WebsiteIndexRedirect = (): React.ReactElement => {
  const gate = useFeatureGate();
  return <Navigate to={firstWebsiteTab(gate.isEnabled)} replace />;
};

/** `/account/website`: the hub while the `website-hub` flag is on, else {@link WebsiteIndexRedirect}, the section as
 *  it was before the hub. Reactive, so the "777" reveal switches it with no reload. */
export const WebsiteIndex = ({ hub }: { hub: React.ReactElement }): React.ReactElement => {
  const { isEnabled } = useFeatureGate();
  return isEnabled(WEBSITE_HUB_FLAG) ? hub : <WebsiteIndexRedirect />;
};

/** Pre-restructure addresses (`/account/creatives`, `/account/blog/...`) moved under the Website section.
 *  Old bookmarks and links keep working: the `from` prefix is swapped for `to`, and the rest of the path,
 *  the query and the hash are carried over. */
export const MovedSectionRedirect = ({ from, to }: { from: string; to: string }): React.ReactElement => {
  const location = useLocation();
  const suffix = location.pathname.slice(from.length);
  return <Navigate to={`${to}${suffix}${location.search}${location.hash}`} replace />;
};
